import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Typography, Badge, Modal } from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined, HeartOutlined, SafetyOutlined, BulbOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import StripeCheckout from '../../components/checkout/StripeCheckout';
import { trackProductView, trackAddToCart, trackPurchase } from '../../../utils/analytics';
import calmiImage from '../../../assets/images/Calmi.png';

const { Title, Text, Paragraph } = Typography;

const CalmiNight = () => {
  const history = useHistory();
  const [cartItems, setCartItems] = useState([]);
  const [checkoutVisible, setCheckoutVisible] = useState(false);

  useEffect(() => {
    trackProductView('lumi-night', 'Lumi Night', 499);
  }, []);

  const product = {
    id: 'lumi-night',
    name: 'Lumi Night',
    price: 499,
    image: calmiImage,
    collection: 'Specialty Collection',
    subtitle: 'The dreamy bedtime bear',
    description: 'Midnight blue bear with star-speckled fur and crescent moon details. Designed specifically for bedtime comfort with darker colors.',
    fullDescription: 'Lumi Night is the ultimate bedtime companion, designed specifically for children who struggle with sleep routines, nighttime anxiety, or bedtime fears. The midnight blue coloring with star-speckled fur and glow-in-the-dark accents create a magical, calming environment. Lumi Night\'s AI conversations focus on bedtime stories, relaxation techniques, and creating positive sleep associations that help children drift off peacefully.',
    specs: { 
      weight: '2.5 lbs', 
      dimensions: '12" x 8" x 6"', 
      color: 'Midnight Blue', 
      texture: 'Star-Speckled Plush',
      batteryLife: '8+ hours',
      charging: 'USB-C',
      ageRange: '4-12 years'
    },
    features: [
      'Midnight blue coloring perfect for bedtime environments',
      'Glow-in-the-dark star accents for magical nighttime comfort',
      'AI conversations focused on sleep and relaxation',
      'Bedtime stories and guided relaxation programs',
      'Extended battery life for all-night comfort',
      'Pajama-soft texture designed for snuggling'
    ],
    idealFor: [
      'Children with bedtime anxiety or fears',
      'Difficult sleep routines and transitions',
      'Nighttime comfort and security',
      'Building positive sleep associations',
      'Children who resist bedtime',
      'Creating magical bedtime experiences'
    ]
  };

  const addToCart = () => {
    trackAddToCart(product.id, product.name, product.price);
    setCartItems([{ ...product, quantity: 1 }]);
    setCheckoutVisible(true);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    trackPurchase(paymentIntent.id, [{ id: product.id, name: product.name, price: product.price, quantity: 1 }], product.price);
    setCheckoutVisible(false);
    setCartItems([]);
    Modal.success({
      title: 'Preorder Confirmed! 🎉',
      content: (
        <div>
          <p>Your {product.name} preorder has been confirmed! It's guaranteed to arrive in summer 2027.</p>
          <p>You'll receive an email confirmation shortly.</p>
          <p><strong>Order ID:</strong> {paymentIntent.id}</p>
        </div>
      ),
    });
  };

  const handlePaymentError = (error) => {
    Modal.error({
      title: 'Payment Failed',
      content: 'There was an issue processing your payment. Please try again.',
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #000428 0%, #004e92 100%)' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #191970 0%, #000080 100%)',
        padding: '20px 0',
        marginBottom: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/')}
            style={{ color: 'white', marginBottom: '10px' }}
          >
            Back to Lumi Family
          </Button>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '32px', marginRight: '15px' }}>🌙</span>
            <div>
              <Title level={2} style={{ color: 'white', margin: 0 }}>
                {product.name}
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '18px' }}>
                {product.subtitle}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Product Overview */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={12}>
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              backgroundColor: '#001122', 
              borderRadius: '12px', 
              height: '500px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '2px solid #4169e1',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Starry background effect */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(2px 2px at 20px 30px, #fff, transparent),
                           radial-gradient(2px 2px at 40px 70px, #fff, transparent),
                           radial-gradient(1px 1px at 90px 40px, #fff, transparent),
                           radial-gradient(1px 1px at 130px 80px, #fff, transparent),
                           radial-gradient(2px 2px at 160px 30px, #fff, transparent)`,
                backgroundRepeat: 'repeat',
                backgroundSize: '200px 100px',
                opacity: 0.6
              }}></div>
              <img 
                src={product.image} 
                alt={product.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  filter: 'sepia(50%) saturate(200%) hue-rotate(220deg) brightness(0.8)',
                  position: 'relative',
                  zIndex: 1
                }}
              />
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <Badge.Ribbon text={product.collection} color="blue">
              <Card style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
                <Title level={1} style={{ marginTop: 0 }}>
                  {product.name}
                </Title>
                <Title level={2} style={{ color: '#e74c3c', marginBottom: '20px' }}>
                  ${product.price} <Text style={{ fontSize: '16px', color: '#7f8c8d' }}>Preorder · Guaranteed delivery in summer 2027</Text>
                </Title>
                <Paragraph style={{ fontSize: '18px', marginBottom: '30px' }}>
                  {product.fullDescription}
                </Paragraph>
                
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  onClick={addToCart}
                  block
                  style={{
                    background: '#e74c3c',
                    borderColor: '#e74c3c',
                    height: '60px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '20px'
                  }}
                >
                  Preorder Now - ${product.price}
                </Button>

                <Card size="small" style={{ backgroundColor: '#001122', color: 'white' }}>
                  <Text strong style={{ color: 'white' }}>📅 Preorder Details:</Text>
                  <ul style={{ marginTop: '8px', marginBottom: 0, color: 'white' }}>
                    <li>✨ Preordering guarantees your Lumi in summer 2027</li>
                    <li>💳 Pay today to reserve yours</li>
                    <li>🔄 Cancel anytime before shipping</li>
                  </ul>
                </Card>
              </Card>
            </Badge.Ribbon>
          </Col>
        </Row>

        {/* Rest of the content with night theme */}
        <Card title="Technical Specifications" style={{ marginBottom: '40px', backgroundColor: 'rgba(255,255,255,0.95)' }}>
          <Row gutter={[24, 16]}>
            {Object.entries(product.specs).map(([key, value]) => (
              <Col xs={12} md={6} key={key}>
                <Text strong>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}:</Text><br/>
                <Text>{value}</Text>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={12}>
            <Card title="Key Features" style={{ height: '100%', backgroundColor: 'rgba(255,255,255,0.95)' }}>
              <ul>
                {product.features.map((feature, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {feature}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Ideal For" style={{ height: '100%', backgroundColor: 'rgba(255,255,255,0.95)' }}>
              <ul>
                {product.idealFor.map((use, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {use}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
        </Row>

        <Title level={2} style={{ textAlign: 'center', marginBottom: '40px', color: 'white' }}>
          Why Choose Lumi Night?
        </Title>
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%', backgroundColor: 'rgba(255,255,255,0.95)' }}>
              <HeartOutlined style={{ fontSize: '48px', color: '#191970', marginBottom: '16px' }} />
              <Title level={4}>Sleep Specialist</Title>
              <Text>
                Specifically designed for bedtime challenges with sleep-focused AI conversations.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%', backgroundColor: 'rgba(255,255,255,0.95)' }}>
              <SafetyOutlined style={{ fontSize: '48px', color: '#000080', marginBottom: '16px' }} />
              <Title level={4}>Magical Comfort</Title>
              <Text>
                Glow-in-the-dark stars and midnight colors create a magical, non-stimulating environment.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%', backgroundColor: 'rgba(255,255,255,0.95)' }}>
              <BulbOutlined style={{ fontSize: '48px', color: '#4169e1', marginBottom: '16px' }} />
              <Title level={4}>Extended Battery</Title>
              <Text>
                Enhanced battery life ensures all-night comfort and support when your child needs it most.
              </Text>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        title=""
        open={checkoutVisible}
        onCancel={() => setCheckoutVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
        destroyOnClose
      >
        <StripeCheckout
          cartItems={cartItems}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </Modal>
    </div>
  );
};

export default CalmiNight;